import React, { useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLiveRef, useSealedState, useUpdateEffect } from 'reakit-utils';

import type { AsyncActionState, rsc, rtk } from '@owl-frontend/redux';
import { enumSet } from '@owl-lib/type-util';
import { SessionContext } from '../context/AppContainer/AppContainer';

type FirstParam<T> = T extends (arg: infer U) => any ? U : never;

// NOTE: this technique will need to be reworked if we use SSR - likely,
// we will need to use react-router `<Outlet>`s in some advanced fashion, or
// rearrange the page layouting components

// Also, this should use a "stack" style of storage, so that, instead of disappearing on unmount, the previously set value is used.

// idea: use https://www.npmjs.com/package/react-bus or https://github.com/aspect-apps/use-redux-effect
// something similar and provide individual components for the places this is used,
// and then hook up events to each other as needed. Would require reaching into
// the outlet to grab the components for the different parts of the page,
// but it would work well. We cant use only redux as there's often component state
// that shouldn't be pushed to the store, like final form's ephemeral state.
//
type ProvideSetState<K extends PropertyKey> = { [P in K]?: (arg: any) => void };
type CreateUseProviderValue = <T extends ProvideSetState<K>, K extends keyof T>(
  context: React.Context<T>,
  key: K
) => (factory: () => FirstParam<T[K]>, deps: React.DependencyList) => void;
export const createUseProviderValue: CreateUseProviderValue =
  (context, key) => (factory, deps) => {
    const setValue = React.useContext(context)[key];
    // eslint-disable-next-line react-hooks2/exhaustive-deps
    const value = React.useMemo(factory, deps);
    React.useLayoutEffect(() => setValue?.(value), [setValue, value]);
    // Clear on unmount
    // eslint-disable-next-line react-hooks2/exhaustive-deps
    React.useLayoutEffect(() => () => setValue?.(undefined), []);
  };

export const finishedStatusSet = enumSet(['fulfilled', 'rejected']);
export type AsyncActionFinished<S extends AsyncActionState> =
  // prettier-align
  Extract<S, { status: 'fulfilled' } | { status: 'rejected' }>;
/**
 * Tracks pending requests by wrapping dispatch, and then calls the callback
 * when the request is complete, based on the corresponding state
 */
export function useAsyncActionDispatch<S extends AsyncActionState>(
  actionState: S,
  callback: (value: AsyncActionFinished<S>) => ReturnType<React.EffectCallback>
): rtk.Dispatch<rsc.AsyncPayloadAction<any>> {
  const dispatch = useDispatch();
  const pending = useSealedState(() => new Set<string>());
  const ref = useLiveRef(callback);
  type F = AsyncActionFinished<S>;
  React.useEffect(() => {
    let requestId: string;
    if (
      finishedStatusSet.has(actionState.status) &&
      pending.has((requestId = (actionState as F).requestId))
    ) {
      pending.delete(requestId);
      return ref.current(actionState as F);
    }
  });
  return React.useCallback((action) => {
    pending.add(action.meta.requestId);
    return dispatch(action);
  }, []);
}

/**
 * Prefer `useAsyncActionDispatch`. Use this to track async actions where you
 * cannot wrap the dispatch function.
 * @example
 * const deleteStatusRaw = useSelector(selectors.actions.deleteForm);
 * useAsyncActionFinished(deleteStatusRaw, (deleteStatus) => {
 *   if (deleteStatus.status === 'fulfilled') {
 *     ...
 *   }
 *   ...
 * });
 */
export function useAsyncActionFinished<S extends AsyncActionState>(
  actionState: S,
  callback: (value: AsyncActionFinished<S>) => ReturnType<React.EffectCallback>
) {
  const finished = useSealedState(() => new Set<string>());
  const ref = useLiveRef(callback);
  type F = AsyncActionFinished<S>;
  useUpdateEffect(() => {
    let requestId: string;
    if (
      finishedStatusSet.has(actionState.status) &&
      !finished.has((requestId = (actionState as F).requestId))
    ) {
      finished.add(requestId);
      return ref.current(actionState as F);
    }
  }, [actionState]);
}

type RouteStateReturn<S> = [S | null, React.Dispatch<S>];
export function useRouteState<S>(): RouteStateReturn<S> {
  const location = useLocation();
  const navigate = useNavigate();
  const setRouteState = React.useCallback<React.Dispatch<S>>(
    (newState) => navigate(location, { replace: true, state: newState }),
    [location, navigate]
  );
  return [location.state as RouteStateReturn<S>[0], setRouteState];
}

/**
 * returns Current User's `manager` role boolean status
 */
type IsManagerState = boolean;
export function useIsManager(): IsManagerState {
  const { current } = useContext(SessionContext);
  if (!current?.role) {
    return false;
  }

  return current.role.toLowerCase() === 'manager';
}

/**
 * returns associated Tenant's SSO status
 */
type HasSSO = boolean;
export function useHasSSO(): HasSSO {
  const { current: currentUser } = useContext(SessionContext);
  return currentUser?.hasSso ?? false;
}
